import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AnimationIcon from "@mui/icons-material/Animation";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import {
  ASSET_RULES,
  AssetKind,
  ThemeAsset,
  deleteAsset,
  listAssets,
  uploadAsset,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/homeThemesService";
import { GREEN, GREEN_DARK, errMsg } from "./themeUtils";

const KINDS: AssetKind[] = ["image", "lottie", "glb", "audio", "video"];

const fmtSize = (b: number | null) =>
  b == null ? "" : b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`;

const AssetThumb: React.FC<{ asset: ThemeAsset }> = ({ asset }) => {
  const box = {
    height: 110,
    borderRadius: 1.5,
    bgcolor: "#F4F7F5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    color: GREEN_DARK,
  } as const;
  switch (asset.kind) {
    case "image":
      return (
        <Box sx={{ ...box, background: "repeating-conic-gradient(#eee 0% 25%, #fff 0% 50%) 50% / 14px 14px" }}>
          <img src={asset.url} alt={asset.name} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
        </Box>
      );
    case "video":
      return (
        <Box sx={box}>
          <video src={asset.url} muted loop playsInline style={{ maxWidth: "100%", maxHeight: "100%" }} onMouseOver={(e) => (e.currentTarget as HTMLVideoElement).play().catch(() => undefined)} />
        </Box>
      );
    case "audio":
      return (
        <Box sx={{ ...box, px: 1 }}>
          <audio src={asset.url} controls style={{ width: "100%" }} />
        </Box>
      );
    case "glb":
      return (
        <Box sx={{ ...box, flexDirection: "column" }}>
          <ViewInArIcon sx={{ fontSize: 40 }} />
          <Typography sx={{ fontSize: 11 }}>3D model</Typography>
        </Box>
      );
    default:
      return (
        <Box sx={{ ...box, flexDirection: "column" }}>
          <AnimationIcon sx={{ fontSize: 40 }} />
          <Typography sx={{ fontSize: 11 }}>Lottie animation</Typography>
        </Box>
      );
  }
};

export const AssetLibrary: React.FC<{
  open: boolean;
  kind?: AssetKind;
  /** Hide the kind tabs (picking for one field). */
  lockKind?: boolean;
  themeId?: number;
  onClose: () => void;
  /** When set, each asset gets a "Use this" button. */
  onSelect?: (url: string) => void;
}> = ({ open, kind: initialKind = "image", lockKind, themeId, onClose, onSelect }) => {
  const [kind, setKind] = React.useState<AssetKind>(initialKind);
  const [assets, setAssets] = React.useState<ThemeAsset[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [confirm, setConfirm] = React.useState<ThemeAsset | null>(null);
  const fileRef = React.useRef<HTMLInputElement | null>(null);

  const load = React.useCallback(async (k: AssetKind) => {
    setLoading(true);
    setError(null);
    try {
      setAssets(await listAssets(k));
    } catch (e) {
      setError(errMsg(e, "Could not load the library. Try again."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open) load(kind);
  }, [open, kind, load]);

  const rule = ASSET_RULES[kind];

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setNotice(null);
    if (file.size > rule.maxMb * 1024 * 1024) {
      setError(`That file is ${fmtSize(file.size)}. ${rule.label} must be ${rule.maxMb} MB or smaller.`);
      return;
    }
    setBusy(true);
    try {
      const a = await uploadAsset(file, kind, themeId);
      setAssets((list) => [a, ...list.filter((x) => x.id !== a.id)]);
      setNotice(`Uploaded ${a.name}.`);
    } catch (e) {
      setError(errMsg(e, "Upload failed. Try again."));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setNotice("Link copied.");
    } catch {
      setNotice(url);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 700 }}>
        {onSelect ? `Pick from the library · ${rule.label.toLowerCase()}` : "Asset library"}
      </DialogTitle>
      <DialogContent dividers>
        {!lockKind && (
          <Tabs
            value={kind}
            onChange={(_, v) => setKind(v)}
            variant="scrollable"
            sx={{ mb: 2, "& .Mui-selected": { color: `${GREEN_DARK} !important` }, "& .MuiTabs-indicator": { bgcolor: GREEN } }}
          >
            {KINDS.map((k) => (
              <Tab key={k} value={k} label={ASSET_RULES[k].label} sx={{ textTransform: "none" }} />
            ))}
          </Tabs>
        )}
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
          <Button
            variant="contained"
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            sx={{ bgcolor: GREEN, "&:hover": { bgcolor: GREEN_DARK }, textTransform: "none", borderRadius: 2 }}
          >
            {busy ? "Uploading…" : `Upload ${kind === "glb" ? "3D model" : kind}`}
          </Button>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            {rule.accept.split(",").filter((x) => x.startsWith(".")).join(", ")} · up to {rule.maxMb} MB
          </Typography>
          <input
            ref={fileRef}
            type="file"
            hidden
            accept={rule.accept}
            onChange={(e) => onFile(e.target.files?.[0])}
          />
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2, wordBreak: "break-all" }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <CircularProgress size={28} sx={{ color: GREEN }} />
          </Box>
        ) : assets.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
            <Typography sx={{ fontWeight: 600 }}>Nothing here yet</Typography>
            <Typography sx={{ fontSize: 13 }}>Upload a file and it will be reusable across all themes.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1.5 }}>
            {assets.map((a) => (
              <Box key={a.id} sx={{ border: "1px solid #E3E8E5", borderRadius: 2, p: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
                <AssetThumb asset={a} />
                <Tooltip title={a.name}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.name}
                  </Typography>
                </Tooltip>
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: -0.5 }}>{fmtSize(a.size_bytes)}</Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {onSelect && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onSelect(a.url)}
                      sx={{ bgcolor: GREEN, "&:hover": { bgcolor: GREEN_DARK }, textTransform: "none", flex: 1, minWidth: 0 }}
                    >
                      Use this
                    </Button>
                  )}
                  <Tooltip title="Copy link">
                    <IconButton size="small" onClick={() => copy(a.url)} aria-label="Copy link">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => setConfirm(a)} aria-label="Delete asset">
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Delete “{confirm?.name}”?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>
            Themes that still use this file will show nothing in its place. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={busy}
            sx={{ textTransform: "none" }}
            onClick={async () => {
              const a = confirm!;
              setBusy(true);
              try {
                await deleteAsset(a.id);
                setAssets((list) => list.filter((x) => x.id !== a.id));
                setConfirm(null);
              } catch (e) {
                setError(errMsg(e, "Could not delete. Try again."));
                setConfirm(null);
              } finally {
                setBusy(false);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AssetLibrary;
